import React from "react";

// Owns the active palette + active drawing color. Consumed by ColorPalette
// (the swatches) and DrawingArea (active color for the hover cursor).
//
// `setPaletteSlot` is forwarded as-is — App passes null in play mode and
// the per-marc setter in author mode, so consumers can null-check to gate
// edit affordances without knowing about author state.
//
// `colorLocked` is a hint to user-action call sites (palette swatch clicks,
// number-key shortcuts) that the active color should be programmatically
// driven and not user-switchable. It's deliberately advisory — the setter
// itself isn't gated, so ScorePanel's auto-color-on-target-change still
// works in speedrun mode where colorLocked is true.
const PaletteContext = React.createContext(null);

export function PaletteProvider({
  children,
  palette,
  setPaletteSlot,
  colorLocked = false,
}) {
  const [activeColorIndex, setActiveColorIndex] = React.useState(0);

  const value = React.useMemo(
    () => ({
      palette,
      activeColorIndex,
      setActiveColorIndex,
      activeColor: palette[activeColorIndex],
      setPaletteSlot,
      colorLocked,
    }),
    [palette, activeColorIndex, setPaletteSlot, colorLocked],
  );

  return (
    <PaletteContext.Provider value={value}>{children}</PaletteContext.Provider>
  );
}

export function usePalette() {
  const ctx = React.useContext(PaletteContext);
  if (!ctx) throw new Error("usePalette must be used inside PaletteProvider");
  return ctx;
}
